import { useMemo, useState } from "react";
import { CalendarPlus, GitMerge, Lock, Timer, X } from "lucide-react";
import { useAuth } from "wasp/client/auth";
import {
  confirmSessionMerge,
  dismissSessionMerge,
  getPendingSessionMerges,
  getTradingSessionsByUser,
  useAction,
  useQuery,
} from "wasp/client/operations";
import { Button } from "../../client/components/ui/button";
import { toast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";
import { useOnboardingStatus } from "../../client/hooks/useOnboardingStatus";
import { calculateSessionStats } from "../../trading-journal/analytics";
import { AddSessionDialog } from "../../trading-sessions/AddSessionDialog";
import { useLiteData } from "../data/LiteDataProvider";
import { inPeriod } from "../lib/filters";
import { EmptyStart } from "../onboarding/EmptyStart";
import { useLiteActions } from "../shell/LiteActions";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { Empty, Stat, cardCls } from "../ui/ui";
import { LastSession } from "./sessions/LastSession";
import { LiveSession } from "./sessions/LiveSession";
import { SessionAnalysis } from "./sessions/SessionAnalysis";
import { SessionHistory } from "./sessions/SessionHistory";
import { useDurationLabel } from "./sessions/SessionBits";
import { byRecent, durationMinutes, isClosedSession, toRow, type RawSession } from "./sessions/sessionData";

/**
 * Sessions, version Lite. Mêmes données et mêmes actions que la page Advanced
 * (grille de widgets) rangées par question : où j'en suis (session en cours ou
 * lancement + bilan de la période), mes sessions (historique filtrable), ce
 * qu'elles m'apprennent (analyses). La personnalisation de la grille n'existe
 * pas en Lite : la mise en page est fixe.
 */
export default function LiteSessionsPage() {
  const t = useTranslation();
  const ts = t.lite.sessions;
  const { data: user } = useAuth();
  const { loading, fmt, scopedTrades, filteredTrades, filters, accountSet, hiddenAccountsCount } = useLiteData();
  const { openUpgrade } = useLiteActions();
  const duration = useDurationLabel();
  const [addOpen, setAddOpen] = useState(false);

  const { data: sessionsData, isLoading: sessionsLoading, refetch: refetchSessions } = useQuery(getTradingSessionsByUser);
  const { data: pendingMerges = [], refetch: refetchMerges } = useQuery(getPendingSessionMerges);
  const confirmMergeFn = useAction(confirmSessionMerge);
  const dismissMergeFn = useAction(dismissSessionMerge);

  // Bandeau d'onboarding : mêmes conditions que la page Advanced.
  const { steps } = useOnboardingStatus(user?.id);
  const sessionStep = steps.find(s => s.id === "session-detail");
  const showOnboardingBanner = sessionStep !== undefined && !sessionStep.completed && !(user as any)?.onboardingCompletedAt;

  const allSessions = useMemo(() => ((sessionsData ?? []) as unknown as RawSession[]), [sessionsData]);
  // Période de la barre commune, sur la date de début (comme le filtre de date Advanced).
  const periodSessions = useMemo(() => allSessions.filter(s => s.startedAt && inPeriod(s.startedAt, filters.period)), [allSessions, filters.period]);
  // Compte / prop firm / stratégie : s'appliquent aux trades rattachés à chaque session.
  const rows = useMemo(
    () => periodSessions.filter(isClosedSession).sort(byRecent).map(s => toRow(s, scopedTrades)),
    [periodSessions, scopedTrades],
  );
  const stats = useMemo(
    () => calculateSessionStats(periodSessions as any, filteredTrades.map(tr => tr.raw)),
    [periodSessions, filteredTrades],
  );

  const handleMerge = async (id: string, accept: boolean) => {
    try {
      await (accept ? confirmMergeFn({ id }) : dismissMergeFn({ id }));
      refetchMerges();
      refetchSessions();
    } catch (e: any) {
      toast({ title: t.common.error, description: e.message, variant: "destructive" });
    }
  };

  // ── Bilan de la période ──
  const n = rows.length;
  const pnl = rows.reduce((s, r) => s + r.pnl, 0);
  const winning = rows.filter(r => r.pnl > 0).length;
  const minutes = rows.map(r => durationMinutes(r.session) ?? 0).filter(m => m > 0);
  const totalMin = minutes.reduce((a, b) => a + b, 0);
  const os = stats.outsideSession;

  const actions = (
    <Button variant="outline" onClick={() => setAddOpen(true)} aria-label={ts.addPast}>
      <CalendarPlus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{ts.addPast}</span>
    </Button>
  );

  const isLoading = loading || sessionsLoading;
  const hasAny = allSessions.length > 0;

  return (
    <>
      <PageHeader title={ts.title} subtitle={ts.subtitle} actions={actions} filters={hasAny ? "full" : false} />
      <PageBody>
        {showOnboardingBanner && (
          <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-medium text-foreground">
            {t.sessions.onboardingBanner}
          </div>
        )}

        {hiddenAccountsCount > 0 && (
          <div className={`${cardCls} px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3`}>
            <Lock size={16} className="text-warning shrink-0" />
            <p className="text-sm text-muted-foreground flex-1">{t.tradingAccounts.hiddenBanner.title.replace("{{count}}", String(hiddenAccountsCount))}</p>
            <Button size="sm" variant="outline" onClick={openUpgrade}>{t.tradingAccounts.hiddenBanner.cta}</Button>
          </div>
        )}

        {/* Fusions proposées par la détection automatique. */}
        {(pendingMerges as any[]).length > 0 && (
          <section className="rounded-2xl border border-warning/40 bg-warning/10 p-4 space-y-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><GitMerge size={16} className="text-warning" />{t.sessions.pendingMergeTitle}</div>
              <p className="text-sm text-muted-foreground mt-1">{t.sessions.pendingMergeSubtitle}</p>
            </div>
            <div className="space-y-2">
              {(pendingMerges as any[]).map(merge => (
                <div key={merge.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-card border border-border px-3 py-2.5">
                  <span className="text-sm text-foreground">
                    {t.sessions.pendingMergeBanner
                      .replace("{start}", fmt.time(new Date(merge.proposedStart).toISOString()))
                      .replace("{end}", fmt.time(new Date(merge.proposedEnd).toISOString()))
                      .replace("{count}", String(merge.tradeCount))}
                  </span>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" onClick={() => handleMerge(merge.id, true)}>{t.sessions.pendingMergeAccept}</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleMerge(merge.id, false)}><X className="h-4 w-4 mr-1" />{t.sessions.pendingMergeDismiss}</Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {isLoading ? (
          <div className={cardCls}><Empty title={t.lite.common.loading} /></div>
        ) : !hasAny ? (
          <>
            <section className={`${cardCls} p-5 md:p-6`}><LiveSession trades={scopedTrades} /></section>
            <EmptyStart
              icon={Timer}
              title={ts.empty.title}
              text={ts.empty.text}
              cta={<Button variant="outline" onClick={() => setAddOpen(true)}><CalendarPlus className="h-4 w-4 mr-1.5" />{ts.addPast}</Button>}
            />
          </>
        ) : (
          <>
            {/* ── 1. Où j'en suis : session en cours ou lancement, puis bilan de la période ── */}
            <section className={`${cardCls} overflow-hidden`}>
              <div className="p-5 md:p-6"><LiveSession trades={scopedTrades} /></div>
              <div className="border-t border-border px-5 md:px-6 py-4">
                <div className="text-xs font-medium text-muted-foreground mb-3">{ts.summary.title}</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-5">
                  <Stat label={ts.summary.sessions} value={n} hint={totalMin ? ts.summary.totalTime(duration(totalMin)) : undefined} />
                  <Stat label={ts.summary.pnl} value={fmt.money(pnl)} tone={pnl > 0 ? "gain" : pnl < 0 ? "loss" : undefined} />
                  <Stat label={ts.summary.winning} value={n ? fmt.pct(winning / n) : ts.summary.noData} hint={n ? ts.summary.winningHint(winning, n) : undefined} />
                  <Stat label={ts.summary.avgDuration} value={minutes.length ? duration(Math.round(totalMin / minutes.length)) : ts.summary.noData} />
                  <Stat label={ts.summary.outside} value={t.lite.common.trades(os.tradeCount)} hint={os.tradeCount ? `${fmt.money(os.totalPnl)} · ${ts.summary.outsideHint(fmt.pct(os.pctOfTotalTrades / 100))}` : undefined} />
                </div>
              </div>
            </section>

            {n === 0 ? (
              <div className={cardCls}><Empty title={ts.empty.period}>{ts.empty.periodHint}</Empty></div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(420px,1fr)] gap-5 items-start">
                <SessionHistory key={`${filters.period}|${filters.accountId ?? ""}|${accountSet}|${filters.firm ?? ""}|${filters.strategyId ?? ""}`} rows={rows} />
                <div className="space-y-5 min-w-0">
                  <LastSession row={rows[0]} />
                  <SessionAnalysis stats={stats} rows={rows} />
                </div>
              </div>
            )}
          </>
        )}
      </PageBody>

      {/* Saisie d'une session passée : la fenêtre Advanced (avec ajout de trades). */}
      <AddSessionDialog open={addOpen} onOpenChange={setAddOpen} onCreated={refetchSessions} />
    </>
  );
}
